import { useEffect, useMemo, useRef, useState } from 'react';

import { isAbortError, searchGlobal } from '@/components/search/globalSearchApi';
import { flattenGroups, groupResults } from '@/components/search/searchResults';
import type {
  GlobalSearchResponse,
  GlobalSearchResult,
  SearchGroup,
  SearchTypeSummary,
} from '@/components/search/types';

/**
 * Must match `MIN_QUERY_LENGTH` in backend/apps/core/search/views.py. Shorter
 * queries are answered with an empty payload there, so firing them is pure
 * waste -- the bar stays silent instead.
 */
export const GLOBAL_SEARCH_MIN_LENGTH = 2;

/** Long enough to swallow a burst of typing, short enough to feel live. */
export const GLOBAL_SEARCH_DEBOUNCE_MS = 250;

export type GlobalSearchFetcher = (
  term: string,
  signal: AbortSignal
) => Promise<GlobalSearchResponse>;

export interface UseGlobalSearchOptions {
  delayMs?: number;
  minLength?: number;
  /** Injectable for tests; defaults to the real `/search/` client. */
  fetcher?: GlobalSearchFetcher;
  /** Skip searching entirely (e.g. the dropdown is closed). */
  enabled?: boolean;
}

export interface UseGlobalSearchState {
  /** Rows grouped by module, in the server's ranking order. */
  groups: SearchGroup[];
  /** The same rows flattened in display order -- what arrow keys index into. */
  flatResults: GlobalSearchResult[];
  isLoading: boolean;
  error: string | null;
  /** The query is below `minLength`; nothing was requested. */
  tooShort: boolean;
  /** A response has landed for the current term (distinguishes "no results" from "not asked yet"). */
  hasSearched: boolean;
  /** The term the current results actually belong to. */
  resultTerm: string;
}

interface InternalState {
  results: GlobalSearchResult[];
  types: SearchTypeSummary[];
  isLoading: boolean;
  error: string | null;
  hasSearched: boolean;
  resultTerm: string;
}

const IDLE: InternalState = {
  results: [],
  types: [],
  isLoading: false,
  error: null,
  hasSearched: false,
  resultTerm: '',
};

function isIdle(state: InternalState): boolean {
  return (
    state.results.length === 0 &&
    state.types.length === 0 &&
    !state.isLoading &&
    state.error === null &&
    !state.hasSearched
  );
}

function messageFor(error: unknown): string {
  const status = (error as { response?: { status?: number } } | null)?.response?.status;
  if (status === 403) return 'You do not have access to search.';
  if (status && status >= 500) return 'Search is temporarily unavailable.';
  return 'Could not run that search.';
}

/**
 * Debounced, race-free cross-module search.
 *
 * Two separate protections, because they fail differently:
 *  - the AbortController cancels the HTTP request that is no longer wanted;
 *  - the sequence counter drops any response that still arrives (an abort can
 *    lose the race, and a cached/adapter-level response may never abort at all).
 * Without the second one, typing "hon" then "honda" can end with the "hon"
 * results on screen under the word "honda" -- the classic search-bar race.
 */
export function useGlobalSearch(
  term: string,
  options: UseGlobalSearchOptions = {}
): UseGlobalSearchState {
  const {
    delayMs = GLOBAL_SEARCH_DEBOUNCE_MS,
    minLength = GLOBAL_SEARCH_MIN_LENGTH,
    fetcher = searchGlobal,
    enabled = true,
  } = options;

  const trimmed = term.trim();
  const tooShort = trimmed.length < minLength;

  const [state, setState] = useState<InternalState>(IDLE);

  // Held in refs so passing an inline fetcher does not restart the search on
  // every render of the calling component.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const requestSeq = useRef(0);

  useEffect(() => {
    if (!enabled || tooShort) {
      // Bump the sequence so anything in flight is ignored when it lands.
      requestSeq.current += 1;
      setState((prev) => (isIdle(prev) ? prev : IDLE));
      return;
    }

    const seq = ++requestSeq.current;
    const controller = new AbortController();

    // Loading starts at the keystroke, not at the request: the debounce window
    // is dead time the user should see as "working", not as stale results.
    setState((prev) => (prev.isLoading && prev.error === null ? prev : { ...prev, isLoading: true, error: null }));

    const timer = setTimeout(() => {
      fetcherRef
        .current(trimmed, controller.signal)
        .then((response) => {
          if (requestSeq.current !== seq) return;
          setState({
            results: response?.results ?? [],
            types: response?.types ?? [],
            isLoading: false,
            error: null,
            hasSearched: true,
            resultTerm: response?.query ?? trimmed,
          });
        })
        .catch((error: unknown) => {
          if (requestSeq.current !== seq) return;
          if (isAbortError(error)) return;
          setState({
            results: [],
            types: [],
            isLoading: false,
            error: messageFor(error),
            hasSearched: true,
            resultTerm: trimmed,
          });
        });
    }, delayMs);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [trimmed, delayMs, minLength, enabled, tooShort]);

  const groups = useMemo(() => groupResults(state.results, state.types), [state.results, state.types]);
  const flatResults = useMemo(() => flattenGroups(groups), [groups]);

  return {
    groups,
    flatResults,
    isLoading: state.isLoading,
    error: state.error,
    tooShort,
    hasSearched: state.hasSearched,
    resultTerm: state.resultTerm,
  };
}
